"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Search, 
  ChevronDown, 
  Menu, 
  X, 
  Facebook, 
  Linkedin, 
  Twitter, 
  Youtube,
  ExternalLink
} from "lucide-react";

interface NavbarProps {
  scrolled?: boolean;
}

export const Navbar: React.FC<NavbarProps> = ({ scrolled: scrolledProp = false }) => {
  const [scrolledState, setScrolledState] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdowns, setMobileDropdowns] = useState<{ [key: string]: boolean }>({});
  const pathname = usePathname();
  const router = useRouter();
  
  const isLightPage = pathname === "/courses";
  const isScrolled = scrolledProp || scrolledState;
  const isLightHeader = isScrolled || isLightPage;

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => {
      setScrolledState(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const menuItems = [
    {
      label: "Academics",
      hasDropdown: true,
      menu: [
        { name: "Departments", url: "https://shivalikcollege.edu.in/departments" },
        { name: "Courses", url: "/courses" },
        { name: "Faculties", url: "https://shivalikcollege.edu.in/faculties" },
      ],
    },
    {
      label: "Admissions",
      hasDropdown: true,
      menu: [
        { name: "Apply Now", url: "https://shivalikcollege.edu.in/apply" },
        { name: "Brochure", url: "https://shivalikcollege.edu.in/prospectus/" },
        { name: "Fee Structure", url: "https://shivalikcollege.edu.in/fees" },
      ],
    },
    { label: "Placements", hasDropdown: false, link: "/placements" },
    { label: "Courses", hasDropdown: false, link: "/courses" },
    { label: "ERP Login", hasDropdown: false, link: "https://erp.shivalikcollege.edu.in/" },
    {
      label: "Research",
      hasDropdown: true,
      menu: [
        { name: "Innovation Cell", url: "https://shivalikcollege.edu.in/innovation" },
        { name: "Projects", url: "https://shivalikcollege.edu.in/projects" },
        { name: "Patents", url: "https://shivalikcollege.edu.in/patents" },
      ],
    },
    {
      label: "Campus Life",
      hasDropdown: true,
      menu: [
        { name: "Hostel Facilities", url: "https://shivalikcollege.edu.in/hostel" },
        { name: "Sports & Events", url: "https://shivalikcollege.edu.in/sports" },
        { name: "Student Clubs", url: "https://shivalikcollege.edu.in/clubs" },
        { name: "Campus Tour", url: "https://shivalikcollege.edu.in/campus-tour" },
      ],
    },
  ];

  const handleLinkClick = (url: string) => {
    if (url.startsWith("http")) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      router.push(url);
    }
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const toggleMobileDropdown = (label: string) => {
    setMobileDropdowns(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  // Nav item hover states for sliding pill
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <>
      {/* TOP BAR */}
      <div className="fixed top-0 inset-x-0 h-10 bg-[#1e1e1e] text-white/80 border-b border-white/5 z-[999] hidden sm:block">
        <div className="max-w-7xl mx-auto h-full flex justify-between items-center px-4 md:px-8 text-xs font-medium font-sans">
          {/* Quick Info Links */}
          <div className="flex items-center gap-6">
            {["About", "Alumni", "Student Area", "Notice & Event", "Gallery"].map((label) => (
              <a
                key={label}
                href="#"
                className="hover:text-red-400 transition-colors duration-200"
              >
                {label}
              </a>
            ))}
          </div>

          {/* Socials & Apply Button */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 border-r border-white/10 pr-4">
              {[
                { icon: <Facebook className="w-4 h-4" />, url: "#" },
                { icon: <Linkedin className="w-4 h-4" />, url: "#" },
                { icon: <Twitter className="w-4 h-4" />, url: "#" },
                { icon: <Youtube className="w-4 h-4" />, url: "#" },
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 rounded-md hover:bg-white/10 hover:text-white transition-all duration-200"
                >
                  {social.icon}
                </a>
              ))}
            </div>

            <a
              href="https://shivalikcollege.edu.in/apply"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-1.5 rounded bg-gradient-to-r from-red-700 to-rose-600 hover:from-rose-600 hover:to-red-700 text-white font-bold tracking-wide uppercase transition-all duration-200 shadow-lg shadow-red-700/20 hover:scale-[1.02]"
            >
              Apply Now
            </a>
          </div>
        </div>
      </div>

      {/* MAIN NAVIGATION BAR */}
      <header
        className={`fixed left-0 right-0 z-[990] transition-all duration-300 font-sans ${
          isScrolled
            ? "top-0 sm:top-0 py-1.5"
            : "top-0 sm:top-10 py-2"
        } ${
          isLightHeader
            ? "bg-white/95 backdrop-blur-md shadow-[0_4px_30px_rgba(0,0,0,0.06)] border-b border-slate-100"
            : "bg-black/10 backdrop-blur-md sm:backdrop-blur-sm border-b border-white/5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex justify-between items-center">
          {/* Logo & School Name */}
          <div
            onClick={() => router.push("/")}
            className="flex items-center gap-3 cursor-pointer group select-none"
          >
            {/* Logo Image */}
            <div className="flex items-center justify-center bg-white p-1 rounded-lg border border-slate-100 shadow-sm transition-transform duration-300 group-hover:scale-105">
              <Image
                src="/images/logo.png"
                alt="Shivalik College Logo"
                width={76}
                height={30}
                className="object-contain h-6.5 w-auto"
              />
            </div>

            {/* School Text */}
            <div className="flex flex-col">
              <span
                className={`font-bold tracking-tight text-sm md:text-base leading-tight transition-colors duration-300 ${
                  isLightHeader ? "text-slate-900" : "text-white"
                }`}
              >
                Shivalik University
              </span>
              <span
                className={`text-[9px] md:text-[10px] uppercase font-semibold tracking-wider transition-colors duration-300 ${
                  isLightHeader ? "text-red-600" : "text-red-400"
                }`}
              >
                of Engineering & Pharmacy
              </span>
            </div>
          </div>

          {/* Desktop Navigation Links */}
          <nav className="hidden lg:flex items-center gap-1.5 relative">
            {menuItems.map((item, idx) => {
              const isActive = item.hasDropdown
                ? activeDropdown === item.label
                : pathname === item.link;

              return (
                <div
                  key={item.label}
                  className="relative py-1"
                  onMouseEnter={() => {
                    if (item.hasDropdown) setActiveDropdown(item.label);
                    setHoveredIndex(idx);
                  }}
                  onMouseLeave={() => {
                    if (item.hasDropdown) setActiveDropdown(null);
                    setHoveredIndex(null);
                  }}
                >
                  {/* Link / Button Element */}
                  <button
                    onClick={() => !item.hasDropdown && item.link && handleLinkClick(item.link)}
                    className={`relative z-10 px-4 py-2 text-[13px] font-semibold flex items-center gap-1 rounded-full cursor-pointer transition-colors duration-300 ${
                      isLightHeader
                        ? isActive 
                          ? "text-red-600" 
                          : "text-slate-700 hover:text-slate-900"
                        : isActive 
                          ? "text-red-400" 
                          : "text-white/80 hover:text-white"
                    }`}
                  >
                    <span>{item.label}</span>
                    {item.hasDropdown && (
                      <ChevronDown
                        className={`w-3.5 h-3.5 transition-transform duration-300 ${
                          activeDropdown === item.label ? "rotate-180" : ""
                        }`}
                      />
                    )}
                  </button>

                  {/* Sliding Hover Active Indicator (Pill background) */}
                  <AnimatePresence>
                    {hoveredIndex === idx && (
                      <motion.span
                        layoutId="navHover"
                        className={`absolute inset-0 rounded-full z-0 pointer-events-none ${
                          isLightHeader ? "bg-slate-100" : "bg-white/10"
                        }`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                  </AnimatePresence>

                  {/* Active Page Dot Indicator */}
                  {!item.hasDropdown && pathname === item.link && (
                    <span
                      className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${
                        isLightHeader ? "bg-red-600" : "bg-red-400"
                      }`}
                    />
                  )}

                  {/* Desktop Dropdown Menu Card */}
                  <AnimatePresence>
                    {item.hasDropdown && activeDropdown === item.label && (
                      <motion.div
                        initial={{ opacity: 0, y: 12, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="absolute top-full left-0 mt-1 min-w-[200px] p-2 rounded-2xl bg-white border border-slate-100 shadow-[0_12px_40px_rgba(0,0,0,0.12)] z-50 overflow-hidden"
                      >
                        {item.menu?.map((subItem) => (
                          <button
                            key={subItem.name}
                            onClick={() => handleLinkClick(subItem.url)}
                            className="w-full text-left px-4 py-2.5 rounded-lg text-xs font-semibold text-slate-700 hover:text-red-600 hover:bg-red-50/50 flex items-center justify-between group transition-all duration-200 cursor-pointer"
                          >
                            <span>{subItem.name}</span>
                            {subItem.url.startsWith("http") && (
                              <ExternalLink className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-red-500" />
                            )}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </nav>

          {/* Right Actions Block */}
          <div className="flex items-center gap-3">
            {/* Search Trigger */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`p-2 rounded-full cursor-pointer transition-colors duration-300 ${
                isLightHeader
                  ? "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                  : "text-white/80 hover:bg-white/10 hover:text-white"
              }`}
              aria-label="Search site"
            >
              <Search className="w-4 h-4 sm:w-5 sm:h-5" />
            </motion.button>

            {/* Apply Button */}
            <a
              href="https://shivalikcollege.edu.in/apply"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex px-5 py-2.5 rounded-full font-bold text-xs uppercase tracking-wider text-white bg-gradient-to-r from-red-700 via-red-600 to-rose-500 shadow-md shadow-red-700/20 hover:scale-[1.03] hover:shadow-lg hover:shadow-red-700/35 transition-all duration-200 transform-gpu"
            >
              Apply Now
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className={`p-2 rounded-full lg:hidden cursor-pointer transition-colors duration-300 ${
                isLightHeader
                  ? "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                  : "text-white/90 hover:bg-white/10 hover:text-white"
              }`}
              aria-label="Open navigation menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE DRAWER */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-[9999] lg:hidden">
            {/* Dark Backdrop Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />

            {/* Drawer Body Container */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 220 }}
              className="absolute right-0 top-0 bottom-0 w-80 max-w-full bg-zinc-950 border-l border-white/5 p-6 flex flex-col justify-between shadow-[0_24px_50px_rgba(0,0,0,0.5)] z-10"
            >
              <div>
                {/* Header with Close */}
                <div className="flex items-center justify-between mb-8 pb-4 border-b border-white/5">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold text-sm uppercase tracking-widest">
                      Shivalik Menu
                    </span>
                  </div>
                  <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="p-2 rounded-full bg-white/5 text-white/70 hover:text-white hover:bg-white/15 cursor-pointer"
                    aria-label="Close navigation menu"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Mobile Drawer Items */}
                <nav className="flex flex-col gap-1.5 overflow-y-auto max-h-[calc(100vh-200px)] pr-2">
                  {menuItems.map((item, i) => {
                    const isDropdownOpen = mobileDropdowns[item.label];

                    return (
                      <div key={item.label} className="w-full">
                        <button
                          onClick={() => 
                            item.hasDropdown 
                              ? toggleMobileDropdown(item.label) 
                              : item.link && handleLinkClick(item.link)
                          }
                          className="w-full py-3 flex items-center justify-between text-[14px] font-semibold text-white/80 hover:text-white border-b border-white/[0.02] cursor-pointer"
                        >
                          <span>{item.label}</span>
                          {item.hasDropdown && (
                            <ChevronDown
                              className={`w-4 h-4 text-white/40 transition-transform duration-300 ${
                                isDropdownOpen ? "rotate-180" : ""
                              }`}
                            />
                          )}
                        </button>

                        {/* Mobile Submenu Dropdown List */}
                        <AnimatePresence>
                          {item.hasDropdown && isDropdownOpen && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.25 }}
                              className="pl-4 overflow-hidden bg-white/[0.02] rounded-lg mt-1"
                            >
                              <div className="py-2 flex flex-col gap-1">
                                {item.menu?.map((subItem) => (
                                  <button
                                    key={subItem.name}
                                    onClick={() => handleLinkClick(subItem.url)}
                                    className="w-full py-2.5 text-left text-xs font-medium text-white/60 hover:text-red-400 flex items-center justify-between pr-4 cursor-pointer"
                                  >
                                    <span>{subItem.name}</span>
                                    {subItem.url.startsWith("http") && (
                                      <ExternalLink className="w-3.5 h-3.5 text-white/30" />
                                    )}
                                  </button>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </nav>
              </div>

              {/* Bottom Quick Info inside Mobile Drawer */}
              <div className="pt-6 border-t border-white/5">
                <a
                  href="https://shivalikcollege.edu.in/apply"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3.5 rounded-full font-bold text-sm text-center text-white bg-gradient-to-r from-red-700 to-rose-600 flex items-center justify-center shadow-lg shadow-red-700/20 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 cursor-pointer"
                >
                  Apply for Admission
                </a>

                {/* Social icons */}
                <div className="flex justify-center gap-5 mt-5 text-white/50">
                  <a href="#" className="hover:text-white transition-colors duration-200"><Facebook className="w-5 h-5" /></a>
                  <a href="#" className="hover:text-white transition-colors duration-200"><Linkedin className="w-5 h-5" /></a>
                  <a href="#" className="hover:text-white transition-colors duration-200"><Twitter className="w-5 h-5" /></a>
                  <a href="#" className="hover:text-white transition-colors duration-200"><Youtube className="w-5 h-5" /></a>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
